import { ELoadingStatus } from "@/app/core/models/loading-status.model";
import { Action, ActionReducer, createReducer, on } from "@ngrx/store";
import {
  loadNotifications,
  loadNotificationsSuccess,
  loadNotificationsFailure,
  loadUnreadCount,
  loadUnreadCountSuccess,
  loadUnreadCountFailure,
  markNotificationAsRead,
  markNotificationAsReadSuccess,
  markNotificationAsReadFailure,
  markAllNotificationsAsRead,
  markAllNotificationsAsReadSuccess,
  markAllNotificationsAsReadFailure,
} from "./notifications.actions";
import { Notification } from "@core/apis/notification.service";

export const NOTIFICATIONS_FEATURE_NAME = "NOTIFICATIONS_FEATURE";

export interface NotificationsState {
  notifications: {
    data: Notification[];
    status: ELoadingStatus;
    error: string;
  };
  unreadCount: {
    data: number;
    status: ELoadingStatus;
    error: string;
  };
  markAllReadInFlight: boolean;
}

const initialState: NotificationsState = {
  notifications: {
    data: [],
    status: ELoadingStatus.idle,
    error: null,
  },
  unreadCount: {
    data: 0,
    status: ELoadingStatus.idle,
    error: null,
  },
  markAllReadInFlight: false,
};

let reducer: ActionReducer<any, Action>;
reducer = createReducer(
  initialState,
  // Load notifications
  on(loadNotifications, (state) => ({
    ...state,
    notifications: {
      ...state.notifications,
      status: ELoadingStatus.loading,
      error: null,
    },
  })),
  on(loadNotificationsSuccess, (state, { notifications }) => ({
    ...state,
    notifications: {
      ...state.notifications,
      data: notifications,
      status: ELoadingStatus.loaded,
    },
  })),
  on(loadNotificationsFailure, (state, { error }) => ({
    ...state,
    notifications: {
      ...state.notifications,
      error,
      status: ELoadingStatus.error,
    },
  })),

  // Load unread count
  on(loadUnreadCount, (state) => ({
    ...state,
    unreadCount: {
      ...state.unreadCount,
      status: ELoadingStatus.loading,
      error: null,
    },
  })),
  on(loadUnreadCountSuccess, (state, { count }) => ({
    ...state,
    unreadCount: {
      ...state.unreadCount,
      data: count,
      status: ELoadingStatus.loaded,
    },
  })),
  on(loadUnreadCountFailure, (state, { error }) => ({
    ...state,
    unreadCount: {
      ...state.unreadCount,
      error,
      status: ELoadingStatus.error,
    },
  })),

  // Mark as read
  on(markNotificationAsRead, (state, { notificationId }) => ({
    ...state,
    notifications: {
      ...state.notifications,
      data: state.notifications.data.map((notification) =>
        notification.id === notificationId ? { ...notification, readAt: new Date() } : notification
      ),
    },
  })),
  on(markNotificationAsReadSuccess, (state, { notification }) => ({
    ...state,
    notifications: {
      ...state.notifications,
      data: state.notifications.data.map((n) => (n.id === notification.id ? notification : n)),
    },
    unreadCount: {
      ...state.unreadCount,
      data: Math.max(0, state.unreadCount.data - 1),
    },
  })),
  on(markNotificationAsReadFailure, (state) => ({
    ...state,
  })),

  on(markAllNotificationsAsRead, (state) => ({
    ...state,
    markAllReadInFlight: true,
  })),
  on(markAllNotificationsAsReadSuccess, (state) => {
    const readAt = new Date();
    return {
      ...state,
      markAllReadInFlight: false,
      notifications: {
        ...state.notifications,
        data: state.notifications.data.map((n) => (n.readAt ? n : { ...n, readAt })),
      },
      unreadCount: {
        ...state.unreadCount,
        data: 0,
      },
    };
  }),
  on(markAllNotificationsAsReadFailure, (state) => ({
    ...state,
    markAllReadInFlight: false,
  }))
);

export function notificationsReducer(state: NotificationsState, action: Action) {
  return reducer(state, action);
}
